import { DeviceRule, DEVICE_RULES, findDeviceRule } from "./deviceRules";
import { getHostInfo } from "./getHostInfo";
import { HostInfo } from "./types";

/** Marca a URL de destino: quem chega redirecionado não redireciona de novo (evita loop). */
export const REDIRECT_PARAM = "meepHostRedirect";

/** Quanto o boot espera o HostInfo quando há regras. Host antigo não pode travar a abertura. */
const BOOT_WAIT_MS = 2500;

let activeRule: DeviceRule | null = null;

/** Regra que valeu no boot (ou `null`), para o app consultar `flags`. */
export const getActiveDeviceRule = (): DeviceRule | null => activeRule;

export const buildRedirectUrl = (target: string, ruleId: string, base: string): string | null => {
  try {
    const url = new URL(target, base);
    url.searchParams.set(REDIRECT_PARAM, ruleId);
    return url.toString();
  } catch (error) {
    return null;
  }
};

/** `true` quando a página atual já é o destino (mesma origem e o caminho começa pelo do destino). */
export const isAlreadyAt = (target: string, current: string): boolean => {
  try {
    const to = new URL(target, current);
    const at = new URL(current);
    return to.origin === at.origin && at.pathname.startsWith(to.pathname);
  } catch (error) {
    return false;
  }
};

export interface DeviceRulesResult {
  info: HostInfo | null;
  rule: DeviceRule | null;
  /** URL para a qual o boot mandou a página; o app não deve renderizar. */
  redirectedTo?: string;
}

const waitHostInfo = (): Promise<HostInfo | null> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), BOOT_WAIT_MS);
    getHostInfo().then((info) => {
      clearTimeout(timer);
      resolve(info);
    });
  });

/**
 * Chamado no boot, antes de renderizar o app. Sem regras, só dispara a leitura
 * do HostInfo em segundo plano e segue. Nunca rejeita.
 */
export const applyDeviceRules = async (
  rules: DeviceRule[] = DEVICE_RULES,
  location: { href: string; replace: (url: string) => void } = window.location
): Promise<DeviceRulesResult> => {
  if (rules.length === 0) {
    getHostInfo();
    return { info: null, rule: null };
  }

  const info = await waitHostInfo();
  if (!info) {
    console.warn("[hostInfo] host não respondeu a tempo; seguindo sem regra de dispositivo");
    return { info: null, rule: null };
  }

  const rule = findDeviceRule(info, rules);
  activeRule = rule;
  if (!rule) return { info, rule: null };

  console.log("[hostInfo] regra de dispositivo:", rule.id, {
    family: info.device.family,
    model: info.device.model,
    os: info.os.version,
  });

  if (rule.redirectUrl) {
    let alreadyRedirected = false;
    try {
      alreadyRedirected = new URL(location.href).searchParams.has(REDIRECT_PARAM);
    } catch (error) {
      alreadyRedirected = false;
    }
    if (!alreadyRedirected && !isAlreadyAt(rule.redirectUrl, location.href)) {
      const target = buildRedirectUrl(rule.redirectUrl, rule.id, location.href);
      if (target) {
        location.replace(target);
        return { info, rule, redirectedTo: target };
      }
    }
  }

  return { info, rule };
};
